/**
 * Shared redirect matching logic — used by RedirectsResolver (runtime),
 * the admin Test button, and conflict detection in the admin UI.
 */

export type RedirectRule = {
  from_path: string;
  to_path: string;
};

export type ResolvedRedirect = {
  rule: RedirectRule;
  /** Final destination as a string — relative path or absolute URL. */
  target: string;
  /** True if `to_path` is an absolute http(s) URL. */
  external: boolean;
};

/**
 * Sentinel `to_path` meaning: "matched, do nothing — let the original 404 stand."
 * Used by the Phase 2A prefix-rewrite builder to mark explicit exceptions to a
 * broader wildcard. A matched __noop__ rule is a TERMINAL match: resolveRedirect
 * returns null and the runtime leaves the request alone. This relies on
 * most-specific-first iteration so the exception always wins over the wildcard.
 */
export const NOOP_TARGET = "__noop__";


/** Normalize a path for matching: lowercase, strip trailing slash (except root),
 *  strip query and hash. Returns "/" for empty input. */
export function normalizePath(p: string): string {
  if (!p) return "/";
  let s = p.trim();
  // Drop hash and query
  const hashIdx = s.indexOf("#");
  if (hashIdx >= 0) s = s.slice(0, hashIdx);
  const qIdx = s.indexOf("?");
  if (qIdx >= 0) s = s.slice(0, qIdx);
  s = s.toLowerCase();
  if (!s.startsWith("/")) s = "/" + s;
  if (s.length > 1 && s.endsWith("/")) s = s.slice(0, -1);
  return s;
}

/** True if rule contains a wildcard `*`. */
export function isPattern(fromPath: string): boolean {
  return fromPath.includes("*");
}

/** Compile a `from_path` wildcard pattern (* = one path segment) into a regex.
 *  Returns null if the pattern is malformed. */
export function compilePattern(fromPath: string): RegExp | null {
  try {
    const norm = normalizePath(fromPath);
    // Escape regex special chars except *
    const escaped = norm.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
    const re = escaped.replace(/\*/g, "([^/]+)");
    return new RegExp("^" + re + "$");
  } catch {
    return null;
  }
}

/** Count `$n` back-references in a `to_path`. Returns the highest n seen, or 0. */
export function maxBackRef(toPath: string): number {
  const matches = toPath.match(/\$(\d+)/g);
  if (!matches) return 0;
  return Math.max(...matches.map((m) => parseInt(m.slice(1), 10)));
}

/** Apply $1..$n captures into a `to_path` template. */
function substituteCaptures(toPath: string, caps: string[]): string {
  return toPath.replace(/\$(\d+)/g, (_, n) => {
    const idx = parseInt(n, 10) - 1;
    return caps[idx] ?? "";
  });
}

/** Merge incoming query string with the target's query string.
 *  Target params win on key collision. */
export function mergeQuery(targetQs: string, incomingQs: string): string {
  if (!incomingQs && !targetQs) return "";
  const target = new URLSearchParams(targetQs.replace(/^\?/, ""));
  const incoming = new URLSearchParams(incomingQs.replace(/^\?/, ""));
  // Start with incoming, overwrite with target
  const out = new URLSearchParams();
  incoming.forEach((v, k) => out.append(k, v));
  target.forEach((v, k) => {
    out.delete(k);
    out.append(k, v);
  });
  const s = out.toString();
  return s ? "?" + s : "";
}

/** Specificity score for sort: more literal segments first; longer literal prefix tiebreak. */
function specificity(fromPath: string): number {
  const segs = fromPath.split("/").filter(Boolean);
  const literalSegs = segs.filter((s) => !s.includes("*")).length;
  const beforeStar = fromPath.indexOf("*");
  const prefixLen = beforeStar < 0 ? fromPath.length : beforeStar;
  return literalSegs * 1000 + prefixLen;
}

/**
 * Resolve an incoming URL against a list of rules.
 * `incomingPath` should be the raw pathname (we normalize it).
 * `incomingSearch` and `incomingHash` are passed through to the merged target.
 *
 * Returns the final target string and the matched rule, or null.
 */
export function resolveRedirect(
  rules: RedirectRule[],
  incomingPath: string,
  incomingSearch = "",
  incomingHash = "",
): ResolvedRedirect | null {
  const norm = normalizePath(incomingPath);

  // 1. Exact match
  const exact = rules.find((r) => !isPattern(r.from_path) && normalizePath(r.from_path) === norm);
  let matched: { rule: RedirectRule; caps: string[] } | null = null;
  if (exact) {
    matched = { rule: exact, caps: [] };
  } else {
    // 2. Pattern match — sort by specificity desc
    const patterns = rules
      .filter((r) => isPattern(r.from_path))
      .map((r) => ({ rule: r, re: compilePattern(r.from_path) }))
      .filter((x): x is { rule: RedirectRule; re: RegExp } => x.re !== null)
      .sort((a, b) => specificity(b.rule.from_path) - specificity(a.rule.from_path));

    for (const { rule, re } of patterns) {
      const m = norm.match(re);
      if (m) {
        matched = { rule, caps: m.slice(1) };
        break;
      }
    }
  }

  if (!matched) return null;

  // Terminal __noop__ match: short-circuit and return null so the request
  // 404s naturally. Specificity ordering above guarantees an explicit
  // __noop__ exception is reached before a broader wildcard.
  if (matched.rule.to_path === NOOP_TARGET) return null;

  const { rule, caps } = matched;

  const substituted = caps.length ? substituteCaptures(rule.to_path, caps) : rule.to_path;
  const external = /^https?:\/\//i.test(substituted);

  let target: string;
  if (external) {
    // For absolute URLs, only substitute captures; do not merge incoming query/hash.
    // (External destinations control their own params.)
    target = substituted;
  } else {
    // Split target into path/query/hash
    const hashIdx = substituted.indexOf("#");
    const beforeHash = hashIdx >= 0 ? substituted.slice(0, hashIdx) : substituted;
    const targetHash = hashIdx >= 0 ? substituted.slice(hashIdx) : "";
    const qIdx = beforeHash.indexOf("?");
    const targetPath = qIdx >= 0 ? beforeHash.slice(0, qIdx) : beforeHash;
    const targetQs = qIdx >= 0 ? beforeHash.slice(qIdx) : "";

    const mergedQs = mergeQuery(targetQs, incomingSearch);
    const finalHash = targetHash || incomingHash || "";
    target = targetPath + mergedQs + finalHash;
  }

  return { rule, target, external };
}

/** Normalize a `from_path` for storage: strips query/hash, lowercases, strips trailing slash.
 *  Returns { value, droppedQuery } so the caller can warn the user. */
export function normalizeFromForStorage(input: string): { value: string; droppedQuery: boolean } {
  const raw = input.trim();
  const droppedQuery = raw.includes("?") || raw.includes("#");
  return { value: normalizePath(raw), droppedQuery };
}

/** Normalize a `to_path` for storage: trim, strip trailing slash if relative.
 *  Preserves case, query, and hash. */
export function normalizeToForStorage(input: string): string {
  let s = input.trim();
  if (!s) return s;
  if (/^https?:\/\//i.test(s)) return s;
  if (!s.startsWith("/")) s = "/" + s;
  // Strip trailing slash only on the path portion
  const hashIdx = s.indexOf("#");
  const beforeHash = hashIdx >= 0 ? s.slice(0, hashIdx) : s;
  const tailHash = hashIdx >= 0 ? s.slice(hashIdx) : "";
  const qIdx = beforeHash.indexOf("?");
  let path = qIdx >= 0 ? beforeHash.slice(0, qIdx) : beforeHash;
  const qs = qIdx >= 0 ? beforeHash.slice(qIdx) : "";
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path + qs + tailHash;
}
